import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Auth } from 'firebase/auth';
import { AuthService } from './auth.service';
import { from, map, catchError, Observable, of, switchMap } from 'rxjs';
import { FIREBASE_AUTH_PROVIDER } from './firebase-auth.client';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    @Inject(FIREBASE_AUTH_PROVIDER) private getFirebaseAuth: () => Auth
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> {
    const token = this.authService.getToken();
    if (token) {
      return true;
    }

    // A session was established at some point and only the short-lived
    // access token expired (see AuthService.getToken()) - the exact case
    // authInterceptor already recovers from on a 401, via the httpOnly
    // refresh cookie. Try that cheap path FIRST: falling straight to a full
    // Firebase re-login here (as this guard used to) raced the
    // interceptor's refresh, and when Firebase's own client session
    // couldn't be silently renewed at that moment, sent the user to /login
    // even though the backend session was still perfectly valid (#309).
    if (this.authService.hasStoredSession()) {
      return this.authService.refreshToken().pipe(
        map(() => true),
        catchError(() => this.recoverViaFirebase(state))
      );
    }

    return this.recoverViaFirebase(state);
  }

  /** Fallback path for when there is no backend session to refresh (never
   * logged in, or the refresh cookie itself is gone/expired/revoked) - only
   * then is a full Firebase-backed re-login or a redirect to /login
   * appropriate. Always returns an Observable so it composes directly
   * inside canActivate's catchError, regardless of which branch fires. */
  private recoverViaFirebase(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const auth = this.getFirebaseAuth();
    // On a fresh page load Firebase restores its persisted user
    // asynchronously; reading currentUser before that finishes sees null and
    // sent a still-signed-in user to /login (#448).
    const ready = typeof auth.authStateReady === 'function' ? auth.authStateReady() : Promise.resolve();
    return from(ready).pipe(
      catchError(() => of(void 0)),
      switchMap(() => this.recoverWithUser(auth, state))
    );
  }

  private recoverWithUser(auth: Auth, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const user = auth.currentUser;
    if (user && !user.emailVerified) {
      return of(
        this.router.createUrlTree(['/verify-email'], {
          queryParams: {
            email: user.email || null,
            returnUrl: state.url,
          },
        })
      );
    }

    if (user && user.emailVerified) {
      return from(user.getIdToken(true)).pipe(
        switchMap((idToken) => this.authService.loginWithFirebaseIdToken(idToken, 'password')),
        map(() => true),
        catchError(() =>
          of(
            this.router.createUrlTree(['/login'], {
              queryParams: { returnUrl: state.url },
            })
          )
        )
      );
    }

    return of(
      this.router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      })
    );
  }
}
